import React, { useState } from 'react';
import styled from 'styled-components';

import { DispensaryMenuBannerColors } from 'shared/constants';

import RichTextHtmlWrapper from 'shared/components/rich-text-html-wrapper';
import SvgChevronIcon from 'assets/chevron-icon';
import { tracker } from 'src/utils/analytics';
import useDispensary from 'src/dispensary/hooks/use-dispensary';

export default function MenuBanner({
  htmlContent,
  color,
  className,
}: {
  htmlContent?: string;
  color: string;
  className?: string;
}): JSX.Element {
  const { dispensary } = useDispensary();

  const isContentLongAndCollapsed =
    (htmlContent?.length ?? 0) > 500 && (dispensary.menuBannerTextBannerCollapsed ?? true);

  const [isCollapsed, setIsCollapsed] = useState(isContentLongAndCollapsed);

  const handleExpand = (): void => {
    setIsCollapsed(false);
    tracker.gaGTMClickEvent({
      eventCategory: `text banner impression`,
      eventAction: `expanded`,
    });
  };

  const handleCollapse = (): void => {
    setIsCollapsed(true);
    tracker.gaGTMClickEvent({
      eventCategory: `text banner impression`,
      eventAction: `collapse`,
    });
  };

  return (
    <Container
      className={className}
      color={color || 'green'}
      isLongHtmlContent={isCollapsed}
      data-cy='menu-banner'
      data-test='menu-banner'
      data-testid='menu-banner'
      onClick={isCollapsed ? handleExpand : undefined}
    >
      <StyledRichTextWrapper isLongHtmlContent={isCollapsed} color={color || 'green'}>
        <RichTextHtmlWrapper html={htmlContent} />
      </StyledRichTextWrapper>
      <StyledChevronWrapper isLongHtmlContent={isCollapsed} onClick={isCollapsed ? handleExpand : handleCollapse}>
        <SvgChevronIcon fill={color} width='var(--dcm-banner-chevron-size)' />
      </StyledChevronWrapper>
    </Container>
  );
}

const Container = styled.div<{ color: string; isLongHtmlContent?: boolean }>`
  background: ${(props) => DispensaryMenuBannerColors[props.color].background};
  border-radius: var(--dcm-banner-border-radius);
  color: ${(props) => DispensaryMenuBannerColors[props.color].color};
  cursor: default;
  display: flex;
  flex-direction: row;
  font-size: var(--dcm-banner-font-size);
  justify-content: space-between;
  line-height: var(--dcm-banner-line-height);
  margin: var(--dcm-banner-margin);
  max-width: var(--dcm-banner-max-width);
  padding: var(--dcm-banner-padding);
  word-break: break-word;

  ${({ theme }) => theme.breakpoints.down('sm')} {
    margin: var(--dcm-banner-margin-sm);
    padding: var(--dcm-banner-padding);
  }

  strong {
    font-weight: bold;
  }

  em {
    font-style: italic;
  }

  a {
    color: var(--dcm-color-primary);
  }

  ${({ isLongHtmlContent }) =>
    isLongHtmlContent &&
    `
      height: var(--dcm-banner-collapsed-height);
      overflow: hidden;
      cursor: pointer;
    `}
`;

const StyledChevronWrapper = styled.div<{ isLongHtmlContent?: boolean }>`
  display: flex;
  height: min-content;
  width: 25px;

  ${({ isLongHtmlContent }) =>
    !isLongHtmlContent &&
    `
      cursor: pointer;
      svg {
       transform: rotate(180deg);
      }
    `}
`;

const StyledRichTextWrapper = styled.div<{ isLongHtmlContent?: boolean; color: string }>`
  ${({ isLongHtmlContent, color }) =>
    isLongHtmlContent &&
    `
      height: var(--dcm-banner-mask-height);
      -webkit-mask-image: linear-gradient(180deg, ${DispensaryMenuBannerColors[color].background} 60%, transparent);

      &:hover {
        -webkit-mask-image: linear-gradient(180deg, ${DispensaryMenuBannerColors[color].background} 100%, transparent);
      }
    `}
`;
